import { Component, type FormEvent, type SyntheticEvent } from "react";
import { observer } from "mobx-react";
import {
    Stack,
    DefaultButton,
    TextField,
    Toggle,
    DatePicker,
    Separator,
    Dropdown,
    type IDropdownOption,
    TooltipHost,
} from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import SearchableDropdown from "../atoms/searchable-dropdown";
import {
    VehicleDeviceStatus,
    type Department,
    ProcurementType,
    type ShockProfile,
    type Vehicle,
    type VehicleDevice,
    VehicleStatus,
    ManagementRole,
    EngineType,
    type VehicleType,
    type VehicleCreate,
} from "../../api";
import { createUuid, type UUID } from "../../utils/uuid";
import { type FormCreateProps } from "../../utils/form-create-props";
import {
    doubleBindIntegerToString,
    doubleBindNullableString,
    doubleBindNullableStringEmptyAsNull,
    doubleBindString,
} from "../../utils/double-bind";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ModalConfirmation } from "../atoms/modal-confirmation";
import { clone, equals } from "ramda";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { userInputToNumber } from "../../utils/user-input-to-number";
import { defaultPageSize } from "../../utils/constants";
import { ClearableDropdown } from "../atoms/clearable-dropdown";

export interface FormCreateVehicleProps extends FormCreateProps<Vehicle> {
    asDialogContent?: boolean;
}

import { store } from "../../store";

@observer
export class FormCreateVehicle extends Component<FormCreateVehicleProps> {
    @observable public accessor manufacturer: string | undefined;
    @observable public accessor model: string | undefined;
    @observable public accessor serialNumber = "";
    @observable public accessor vehicleTypeId: UUID | null | undefined;
    @observable public accessor departmentId: UUID | null | undefined;
    @observable public accessor manufactureYear: number | null | undefined;
    @observable public accessor shockProfileId: UUID | null | undefined;
    @observable public accessor magneticFieldThreshold: number = 100;
    @observable public accessor motionStartThresholdMv: number = 14500;
    @observable public accessor motionStopThresholdMv: number = 12000;
    @observable public accessor motionStartDelayMs: number = 1500;
    @observable public accessor motionStopDelayMs: number = 1500;
    @observable public accessor preOpsChecklistId: UUID | null | undefined;

    @observable public accessor maintenanceLockdown = false;
    @observable public accessor checklistOverride = false;

    @observable public accessor initialOperatingHours = "";
    @observable public accessor idleAutoLogoutAfter = "10";
    @observable public accessor maintenanceInterval = "100";
    @observable public accessor maintenanceNotificationAt = "";
    @observable public accessor weeklyDriveGoalHours = "";

    @observable public accessor procurementType: ProcurementType | undefined;
    @observable public accessor purchaseDate: Date | undefined;
    @observable public accessor purchaseCost: number | null | undefined;
    @observable public accessor contractTermStart: Date | undefined;
    @observable public accessor contractTermEnd: Date | undefined;
    @observable public accessor monthlyCost: number | null | undefined;
    @observable public accessor lastMaintenance: Date | undefined;

    @observable public accessor status = VehicleStatus.ACTIVE;
    @observable public accessor phoneImei: string | undefined | null;

    @observable public accessor vehicleDeviceId: UUID | null | undefined;

    @observable public accessor engineType = EngineType.ELECTRIC;

    @observable private accessor cancelConfirmationModalVisible = false;

    private validationId = createUuid();
    private cleanState: VehicleCreate;

    constructor(props: FormCreateVehicleProps) {
        super(props);
        makeObservable(this);
        this.cleanState = clone(this.vehicleCreate);
    }

    private triggerValidation(): void {
        store.repositoryVehicles.validation.create.updateModel(
            this.validationId,
            this.vehicleCreate,
        );
    }

    componentDidMount(): void {
        store.repositoryVehicles.validation.create.initializeModel(
            this.validationId,
            this.vehicleCreate,
        );
    }

    @computed private get vehicleCreate(): VehicleCreate {
        const {
            manufacturer,
            model,
            vehicleTypeId,
            serialNumber,
            departmentId,
            manufactureYear,
            shockProfileId,
            preOpsChecklistId,

            maintenanceLockdown,
            checklistOverride,

            initialOperatingHours,
            idleAutoLogoutAfter,
            maintenanceInterval,
            maintenanceNotificationAt,
            weeklyDriveGoalHours,

            procurementType,
            purchaseDate,
            purchaseCost,
            contractTermStart,
            contractTermEnd,
            monthlyCost,
            phoneImei,
            engineType,

            magneticFieldThreshold,
            motionStartThresholdMv,
            motionStopThresholdMv,
            motionStartDelayMs,
            motionStopDelayMs,
        } = this;

        return {
            manufacturer,
            model,
            vehicleTypeId,
            serialNumber,
            manufactureYear,
            shockProfileId,
            departmentId,
            preOpsChecklistId,

            maintenanceLockdown,
            checklistOverride,

            initialOperatingHours: userInputToNumber(initialOperatingHours, 0),
            idleAutoLogoutAfter: userInputToNumber(idleAutoLogoutAfter, undefined),
            maintenanceInterval: userInputToNumber(maintenanceInterval, 100),
            maintenanceNotificationAt: userInputToNumber(maintenanceNotificationAt, undefined),
            weeklyDriveGoalHours: userInputToNumber(weeklyDriveGoalHours, undefined),

            procurementType,
            purchaseDate,
            purchaseCost,
            contractTermStart,
            contractTermEnd,
            monthlyCost,
            phoneImei,
            engineType,

            magneticFieldThreshold,
            motionStartThresholdMv,
            motionStopThresholdMv,
            motionStartDelayMs,
            motionStopDelayMs,
        };
    }

    @action.bound
    private async createVehicle(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const { vehicleCreate } = this;
        if (!vehicleCreate.serialNumber) {
            throw new Error("Can't submit form: Not fully filled.");
        }
        const vehicle = await store.repositoryVehicles.create(this.vehicleCreate);

        if (this.vehicleDeviceId != null) {
            await store.repositoryVehicleDevices.update(this.vehicleDeviceId, {
                vehicleId: vehicle.id,
            });
            await store.repositoryVehicleDevices.reloadQuery({
                pageSize: defaultPageSize,
                vehicleId: vehicle.id,
            });
            await store.repositoryVehicleDevices.reloadQuery({
                pageSize: defaultPageSize,
                status: VehicleDeviceStatus.NOT_ASSIGNED,
            });
        }

        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(vehicle);
        }
    }

    @action.bound private reset(): void {
        this.triggerValidation();
    }

    @action.bound private setVehicleType(vehicleTypeId?: UUID): void {
        this.vehicleTypeId = vehicleTypeId ?? null;
        this.triggerValidation();
    }

    @action.bound private setShockProfile(shockProfileId?: UUID): void {
        this.shockProfileId = shockProfileId ?? null;
        this.triggerValidation();
    }

    @action.bound private setDepartment(departmentId?: UUID): void {
        this.departmentId = departmentId ?? null;
        this.triggerValidation();
    }

    @action.bound private setChecklistOverride(_event: unknown, flag: boolean | undefined): void {
        this.checklistOverride = flag ?? false;
        this.triggerValidation();
    }

    @action.bound private setMaintenanceLockdown(_event: unknown, flag: boolean | undefined): void {
        this.maintenanceLockdown = flag ?? false;
        this.triggerValidation();
    }

    @action.bound private setManufactureYear(
        _event: FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
        _index?: number,
        _value?: string,
    ): void {
        option == undefined
            ? (this.manufactureYear = null)
            : (this.manufactureYear = option?.key as number);
        this.triggerValidation();
    }

    @action.bound private setPreOpsChecklist(preOpsChecklistId?: UUID): void {
        this.preOpsChecklistId = preOpsChecklistId ?? null;
        this.triggerValidation();
    }

    /**
     * This is run, if the user clicks the form's cancel button.
     * A confirmation modal might pop up, if the user did any changes.
     */
    @action.bound private showConfirmAndCallDialogCancelCallback(): void {
        // If there were any changes, i.e. if the original and mutable copy aren't the same,
        // open the confirmation modal for aborting the update process.
        if (!equals(this.cleanState, this.vehicleCreate)) {
            this.cancelConfirmationModalVisible = true;
            return;
        }

        this.props.onDialogCancel();
    }

    @action.bound private setPhoneImei(
        _event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined,
    ): void {
        this.phoneImei = newValue == "" ? null : newValue;
        this.triggerValidation();
    }

    @action.bound private setProcurementType(key?: string): void {
        this.procurementType =
            ProcurementType[String(key).toUpperCase() as keyof typeof ProcurementType] || null;
        this.triggerValidation();
    }

    @action.bound private setPurchaseDate(date: Date | null | undefined): void {
        this.purchaseDate = date ?? undefined;
        this.triggerValidation();
    }

    @action.bound private setContractTermStart(date: Date | null | undefined): void {
        this.contractTermStart = date ?? undefined;
        this.triggerValidation();
    }

    @action.bound private setContractTermEnd(date: Date | null | undefined): void {
        this.contractTermEnd = date ?? undefined;
        this.triggerValidation();
    }

    @action.bound private setVehicleDevice(vehicleDeviceId?: UUID): void {
        this.vehicleDeviceId = vehicleDeviceId ?? null;
        this.triggerValidation();
    }

    @action.bound private closeCancelConfirmationModalVisible(): void {
        this.cancelConfirmationModalVisible = false;
    }

    @action.bound private setEngineType(
        _event: FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
        _index?: number,
    ): void {
        this.engineType =
            EngineType[String(option!.key).toUpperCase() as keyof typeof EngineType] ||
            EngineType.ELECTRIC;
        this.triggerValidation();
    }

    public get yearOptions(): IDropdownOption[] {
        const options: IDropdownOption[] = [];
        const currentYear = new Date().getFullYear();
        for (let i = currentYear; i > 1924; i--) {
            options.push({ key: i, text: i.toString() });
        }
        return options;
    }

    public onFormatDate = (date?: Date): string => {
        return store.i18n.formatDateOnly(date!);
    };

    public renderGeneralSection(): JSX.Element {
        const engineTypeOptions: IDropdownOption[] = [
            {
                key: "electric",
                text: store.i18n.t("formCreateVehicle.engineTypeOptions.electric"),
            },
            {
                key: "combustion",
                text: store.i18n.t("formCreateVehicle.engineTypeOptions.combustion"),
            },
        ];
        return (
            <Stack
                horizontal
                horizontalAlign="space-between"
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Stack>
                    <TextField
                        label={store.i18n.t("formCreateVehicle.id.label")}
                        {...doubleBindString<FormCreateVehicle>(this, "serialNumber", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "serialNumber",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t("formCreateVehicle.manufacturer.label")}
                        {...doubleBindNullableStringEmptyAsNull<FormCreateVehicle>(
                            this,
                            "manufacturer",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "manufacturer",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t("formCreateVehicle.model.label")}
                        {...doubleBindNullableStringEmptyAsNull<FormCreateVehicle>(
                            this,
                            "model",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "model",
                            ),
                        )}
                    />
                    <SearchableDropdown
                        formatEntity={(vehicleType: VehicleType) =>
                            store.i18n.formatVehicleType(vehicleType)
                        }
                        repository={store.repositoryVehicleTypes}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formCreateVehicle.vehicleType.label")}
                        onChange={this.setVehicleType}
                        selectedKey={this.vehicleTypeId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "vehicleTypeId",
                            ),
                        )}
                        hideSearch
                        clearable
                    />
                    <Dropdown
                        label={store.i18n.t("formCreateVehicle.yearOfBuild.label")}
                        selectedKey={this.manufactureYear}
                        onChange={this.setManufactureYear}
                        options={this.yearOptions}
                        calloutProps={{ directionalHintFixed: true }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "manufactureYear",
                            ),
                        )}
                    />
                    <SearchableDropdown
                        formatEntity={(department: Department) =>
                            store.i18n.formatDepartment(department)
                        }
                        repository={store.repositoryDepartments}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formCreateVehicle.department.label")}
                        onChange={this.setDepartment}
                        selectedKey={this.departmentId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "departmentId",
                            ),
                        )}
                        clearable
                    />
                    <SearchableDropdown
                        formatEntity={store.i18n.formatPreOpsChecklist}
                        repository={store.repositoryPreOpsChecklists}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formCreateVehicle.preOpsChecklist.label")}
                        onChange={this.setPreOpsChecklist}
                        selectedKey={this.preOpsChecklistId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "preOpsChecklistId",
                            ),
                        )}
                        clearable
                    />
                </Stack>
                <Stack>
                    <Toggle
                        label={store.i18n.t("formCreateVehicle.checklistOverride.label")}
                        checked={this.checklistOverride}
                        onChange={this.setChecklistOverride}
                    />
                    <SearchableDropdown
                        formatEntity={(shockProfile: ShockProfile) => shockProfile.label}
                        repository={store.repositoryShockProfiles}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formCreateVehicle.shockProfile.label")}
                        onChange={this.setShockProfile}
                        selectedKey={this.shockProfileId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "shockProfileId",
                            ),
                        )}
                        hideSearch
                        clearable
                    />
                    <TextField
                        type="number"
                        label={store.i18n.t("formCreateVehicle.idleAutoLogoutAfter.label")}
                        {...doubleBindString<FormCreateVehicle>(this, "idleAutoLogoutAfter", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "idleAutoLogoutAfter",
                            ),
                        )}
                    />
                    <Dropdown
                        label={store.i18n.t("formCreateVehicle.engineTypeOptions.label")}
                        options={engineTypeOptions}
                        selectedKey={this.engineType}
                        onChange={this.setEngineType}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "engineType",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.engineType === EngineType.COMBUSTION
                        }
                        label={store.i18n.t("formCreateVehicle.magneticFieldThreshold.label")}
                        type="number"
                        {...doubleBindIntegerToString<FormCreateVehicle>(
                            this,
                            "magneticFieldThreshold",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "magneticFieldThreshold",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.engineType === EngineType.ELECTRIC
                        }
                        label={store.i18n.t("formCreateVehicle.motionStartThresholdMv.label")}
                        type="number"
                        {...doubleBindIntegerToString<FormCreateVehicle>(
                            this,
                            "motionStartThresholdMv",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "motionStartThresholdMv",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.engineType === EngineType.ELECTRIC
                        }
                        label={store.i18n.t("formCreateVehicle.motionStopThresholdMv.label")}
                        type="number"
                        {...doubleBindIntegerToString<FormCreateVehicle>(
                            this,
                            "motionStopThresholdMv",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "motionStopThresholdMv",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.engineType === EngineType.ELECTRIC
                        }
                        label={store.i18n.t("formCreateVehicle.motionStartDelayMs.label")}
                        type="number"
                        {...doubleBindIntegerToString<FormCreateVehicle>(
                            this,
                            "motionStartDelayMs",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "motionStartDelayMs",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.engineType === EngineType.ELECTRIC
                        }
                        label={store.i18n.t("formCreateVehicle.motionStopDelayMs.label")}
                        type="number"
                        {...doubleBindIntegerToString<FormCreateVehicle>(
                            this,
                            "motionStopDelayMs",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "motionStopDelayMs",
                            ),
                        )}
                    />
                </Stack>
            </Stack>
        );
    }

    public renderFinancingSection(): JSX.Element {
        const procurementOptions: IDropdownOption[] = [
            {
                key: "purchase",
                text: store.i18n.t("formCreateVehicle.procurementOptions.purchase"),
            },
            {
                key: "leasing",
                text: store.i18n.t("formCreateVehicle.procurementOptions.leasing"),
            },
            {
                key: "rental",
                text: store.i18n.t("formCreateVehicle.procurementOptions.rental"),
            },
        ];
        return (
            <Stack
                horizontal
                horizontalAlign="space-between"
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Stack>
                    <ClearableDropdown
                        label={store.i18n.t("formCreateVehicle.procurementOptions.label")}
                        onChange={this.setProcurementType}
                        options={procurementOptions}
                        selectedKey={this.procurementType}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "procurementType",
                            ),
                        )}
                    />
                    <DatePicker
                        disabled={this.procurementType !== ProcurementType.PURCHASE}
                        isRequired={this.procurementType === ProcurementType.PURCHASE}
                        formatDate={this.onFormatDate}
                        placeholder={store.i18n.t("formCreateVehicle.datePicker.placeholder")}
                        label={store.i18n.t("formCreateVehicle.purchaseDate.label")}
                        onSelectDate={this.setPurchaseDate}
                    />
                    <DatePicker
                        disabled={
                            this.procurementType !== ProcurementType.LEASING &&
                            this.procurementType !== ProcurementType.RENTAL
                        }
                        isRequired={
                            this.procurementType === ProcurementType.LEASING ||
                            this.procurementType === ProcurementType.RENTAL
                        }
                        maxDate={this.contractTermEnd}
                        formatDate={this.onFormatDate}
                        placeholder={store.i18n.t("formCreateVehicle.datePicker.placeholder")}
                        label={store.i18n.t("formCreateVehicle.contractTermStart.label")}
                        onSelectDate={this.setContractTermStart}
                        strings={store.i18n.datePickerStrings(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "contractTermStart",
                            ),
                        )}
                    />
                </Stack>
                <Stack>
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.purchasePrice.label")}
                        styles={{ root: { visibility: "hidden" } }}
                    />
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.purchasePrice.label")}
                        styles={{ root: { visibility: "hidden" } }}
                    />
                    <DatePicker
                        disabled={
                            this.procurementType !== ProcurementType.LEASING &&
                            this.procurementType !== ProcurementType.RENTAL
                        }
                        isRequired={
                            this.procurementType === ProcurementType.LEASING ||
                            this.procurementType === ProcurementType.RENTAL
                        }
                        formatDate={this.onFormatDate}
                        placeholder={store.i18n.t("formCreateVehicle.datePicker.placeholder")}
                        label={store.i18n.t("formUpdateVehicle.contractTermEnd.label")}
                        onSelectDate={this.setContractTermEnd}
                        strings={store.i18n.datePickerStrings(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "contractTermEnd",
                            ),
                        )}
                    />
                </Stack>
                <Stack>
                    <TooltipHost content={store.i18n.t("generic.comingSoon")}>
                        <TextField
                            disabled
                            label={store.i18n.t("formUpdateVehicle.contractFile.label")}
                        />
                    </TooltipHost>
                    <TextField
                        disabled={this.procurementType !== ProcurementType.PURCHASE}
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.purchasePrice.label")}
                        suffix="$"
                        {...doubleBindIntegerToString<FormCreateVehicle>(this, "purchaseCost", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "purchaseCost",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            this.procurementType !== ProcurementType.LEASING &&
                            this.procurementType !== ProcurementType.RENTAL
                        }
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.monthlyCost.label")}
                        suffix="$"
                        {...doubleBindIntegerToString<FormCreateVehicle>(this, "monthlyCost", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "monthlyCost",
                            ),
                        )}
                    />
                </Stack>
            </Stack>
        );
    }

    public renderMaintenanceSection(): JSX.Element {
        return (
            <Stack
                horizontal
                horizontalAlign="space-between"
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Stack>
                    <TextField
                        type="number"
                        label={store.i18n.t("formCreateVehicle.initialOperatingHours.label")}
                        {...doubleBindString<FormCreateVehicle>(this, "initialOperatingHours", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "initialOperatingHours",
                            ),
                        )}
                    />
                    <DatePicker
                        formatDate={this.onFormatDate}
                        disabled
                        placeholder={store.i18n.t("formCreateVehicle.datePicker.placeholder")}
                        label={store.i18n.t("formCreateVehicle.lastMaintenance.label")}
                        strings={store.i18n.datePickerStrings(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "contractTermEnd",
                            ),
                        )}
                    />
                    <TextField
                        type="number"
                        label={store.i18n.t("formCreateVehicle.maintenanceNotificationAt.label")}
                        {...doubleBindString<FormCreateVehicle>(
                            this,
                            "maintenanceNotificationAt",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "maintenanceNotificationAt",
                            ),
                        )}
                    />
                </Stack>
                <Stack>
                    <TextField
                        disabled
                        label={store.i18n.t("formCreateVehicle.operatingHours.label")}
                    />
                    <TextField
                        disabled
                        label={store.i18n.t("formCreateVehicle.hoursSinceLastMaintenance.label")}
                    />
                    <Toggle
                        label={store.i18n.t("formCreateVehicle.maintenanceLockdown.label")}
                        checked={this.maintenanceLockdown}
                        onChange={this.setMaintenanceLockdown}
                    />
                </Stack>
                <Stack>
                    <TextField
                        type="number"
                        label={store.i18n.t("formCreateVehicle.maintenanceInterval.label")}
                        {...doubleBindString<FormCreateVehicle>(this, "maintenanceInterval", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "maintenanceInterval",
                            ),
                        )}
                    />
                    <TextField
                        disabled
                        label={store.i18n.t("formCreateVehicle.hoursUntilMaintenance.label")}
                    />
                    <TextField
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.weeklyDriveTimeGoal.label")}
                        {...doubleBindString<FormCreateVehicle>(this, "weeklyDriveGoalHours", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "weeklyDriveGoalHours",
                            ),
                        )}
                    />
                </Stack>
            </Stack>
        );
    }

    public renderMiscellaneousSection(): JSX.Element {
        return (
            <Stack
                horizontal
                horizontalAlign="space-between"
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Stack>
                    <TextField
                        disabled
                        label={store.i18n.t("formCreateVehicle.currentStatus.label")}
                    />
                    <TextField
                        disabled
                        label={store.i18n.t("formCreateVehicle.lastLocation.label")}
                    />
                </Stack>
                <Stack>
                    <TextField
                        disabled
                        label={store.i18n.t("formCreateVehicle.lastDriver.label")}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole != ManagementRole.SUPER_ADMIN
                        }
                        label={store.i18n.t("formCreateVehicle.phoneImei.label")}
                        {...doubleBindNullableString<FormCreateVehicle>(this, "phoneImei", () =>
                            this.triggerValidation(),
                        )}
                        onChange={this.setPhoneImei}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.create.getFieldValidationState(
                                this.validationId,
                                "phoneImei",
                            ),
                        )}
                    />
                </Stack>
                <Stack>
                    <TextField
                        disabled
                        label={store.i18n.t("formCreateVehicle.lastSynchronization.label")}
                    />
                    <SearchableDropdown
                        disabled={
                            store.serviceOwnUser.user?.managementRole != ManagementRole.SUPER_ADMIN
                        }
                        label={store.i18n.t("formCreateVehicle.vehicleDevice.label")}
                        formatEntity={(vehicleDevice: VehicleDevice) => vehicleDevice.serialNumber}
                        repository={store.repositoryVehicleDevices}
                        query={{
                            pageSize: defaultPageSize,
                            status: VehicleDeviceStatus.NOT_ASSIGNED,
                        }}
                        onChange={this.setVehicleDevice}
                        selectedKey={this.vehicleDeviceId}
                        clearable
                    />
                </Stack>
            </Stack>
        );
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateVehicle.submit.text")}
                validation={store.repositoryVehicles.validation.create}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.createVehicle}>
                <Stack
                    horizontal
                    horizontalAlign="space-between"
                    tokens={{
                        childrenGap: "1em",
                        padding: `0px ${sizes.formPaddingHorizontal}`,
                    }}
                >
                    <Stack>
                        <h2>{store.i18n.t("formCreateVehicle.title.general")}</h2>
                        {this.renderGeneralSection()}
                        <Separator />
                        <h2>{store.i18n.t("formCreateVehicle.title.financing")}</h2>
                        {this.renderFinancingSection()}
                    </Stack>
                    <Separator vertical />
                    <Stack>
                        <h2>{store.i18n.t("formCreateVehicle.title.maintenance")}</h2>
                        {this.renderMaintenanceSection()}
                        <Separator />
                        <h2>{store.i18n.t("formCreateVehicle.title.miscellaneous")}</h2>
                        {this.renderMiscellaneousSection()}
                    </Stack>
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreateVehicle.cancel.text")}
                            onClick={this.showConfirmAndCallDialogCancelCallback}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
                {
                    <ModalConfirmation
                        isOpen={this.cancelConfirmationModalVisible}
                        title={store.i18n.t("modalAbortUpdate.title")}
                        text={store.i18n.t("modalAbortUpdate.description")}
                        onConfirm={this.props.onDialogCancel}
                        onCancel={this.closeCancelConfirmationModalVisible}
                    />
                }
            </form>
        );
    }
}
